// Document Shape Telemetry — admin-only aggregate reporting over the
// document-profiles table (see document-profile-contract.md, the
// authoritative wire contract this file was written against). Every field
// here describes a CARD DESIGN shared by millions of holders, never a
// person or a single scan — the storage layer is one upserted row per
// distinct profile tuple, with no foreign key to a scan, device, org, or
// user. Endpoint paths/shapes below are this dashboard's best-effort read
// of that contract; reconcile against the API once it ships (see this
// file's PR notes for what would need to change if names differ).

import { apiGet } from './client';

export type ScanMethod = 'barcode' | 'passport' | 'mdl';

// The five states a raw complianceIndicator (DDA) value can resolve to.
// Pre-bucketed server-side for the coverage/DDA-presence reports, since the
// server owns the "is this a single A-Z char" test that produces them (see
// the contract's field rules) — the client should not re-derive this
// bucketing independently for those two reports. See classifyCompliance in
// document-profile-support.ts for the one place the client DOES need to
// classify a raw value itself (the newly-seen-profiles list, which carries
// the raw wire value).
export type ComplianceState = 'ABSENT' | 'F' | 'N' | 'M' | 'OTHER';

// ── Report 1: coverage matrix (jurisdiction x AAMVA version x DDA state) ──
// Barcode-scoped only — aamvaVersion and complianceIndicator are AAMVA
// DL/ID concepts with no passport or mDL equivalent (mDL's elementTags are
// ProximityReader field names, not AAMVA tags; passports have no DDA at
// all). One cell per (jurisdiction, aamvaVersion, complianceState) combo
// actually observed — combos with zero observations are simply absent from
// the array, not sent as zero rows.
export interface CoverageCell {
  jurisdiction: string;
  aamvaVersion: number | null;
  complianceState: ComplianceState;
  profiles: number; // distinct card designs (profileHash rows) in this cell
  observations: number; // summed observationCount across those profiles
}

// ── Report 2: DDA presence rate ────────────────────────────────────────────
export interface DdaPresenceRow {
  jurisdiction: string;
  observations: number;
  present: number; // complianceIndicator was non-null
  absent: number;
  presentRate: number; // present / observations, 0..1; NaN-safe (0 if observations is 0)
}

export interface DdaPresenceSummary {
  overall: DdaPresenceRow; // jurisdiction: "ALL"
  byJurisdiction: DdaPresenceRow[];
}

// ── Reports 3 & 4: element tag coverage / unparsed tags ───────────────────
// Same shape for both — "tags Check ID parses" and "tags it doesn't" are
// the same fact table sliced two ways. elementTags/unparsedTags below are
// each already filtered to their respective set; this dashboard doesn't
// re-derive that split client-side.
export interface TagFrequencyRow {
  tag: string;
  scanMethod: ScanMethod;
  jurisdictions: string[]; // sorted, deduped — which jurisdictions emit this tag
  profiles: number;
  observations: number;
  firstSeenOn: string; // date-only (YYYY-MM-DD)
  lastSeenOn: string;
}

// ── Report 5: subfile designators seen ─────────────────────────────────────
export interface SubfileDesignatorRow {
  subfileType: string; // e.g. "DL", "ID", "EN"
  scanMethod: ScanMethod;
  jurisdictions: string[];
  profiles: number;
  observations: number;
  firstSeenOn: string;
  lastSeenOn: string;
}

export interface DocumentProfileSummary {
  generatedAt: string;
  totalObservations: number;
  totalProfiles: number; // distinct card designs seen, all time
  jurisdictionCount: number;
  /** Most recent day any profile arrived (date only), or null if none ever has. */
  lastReceivedOn: string | null;
  coverageMatrix: CoverageCell[];
  ddaPresence: DdaPresenceSummary;
  elementTags: TagFrequencyRow[];
  unparsedTags: TagFrequencyRow[];
  subfileDesignators: SubfileDesignatorRow[];
}

export const getDocumentProfileSummary = (): Promise<DocumentProfileSummary> =>
  apiGet<DocumentProfileSummary>('/admin/document-profiles/summary');

// ── Report 6: newly seen profiles over time ────────────────────────────────
export interface NewProfileRow {
  profileHash: string;
  scanMethod: ScanMethod;
  jurisdiction: string;
  country: string;
  aamvaVersion: number | null;
  subfileType: string | null;
  complianceIndicator: string | null; // raw wire value — see classifyCompliance
  cardRevisionYear: number | null;
  limitedDuration: boolean | null;
  elementTags: string[];
  unparsedTags: string[];
  observationCount: number;
  firstSeenOn: string;
  lastSeenOn: string;
}

export interface NewProfilesResult {
  sinceDays: number;
  items: NewProfileRow[]; // ordered by firstSeenOn desc
  dailyCounts: { date: string; newProfiles: number }[];
}

export const getNewDocumentProfiles = (sinceDays: number): Promise<NewProfilesResult> =>
  apiGet<NewProfilesResult>('/admin/document-profiles/new', { sinceDays });
